import gql from "graphql-tag";
import Vue from "vue";
import IRIS, { createIri } from "@/iris";
import LocalBookFragments from "@/graphql/LocalBookFragments";

export default class ProductInfo {
	constructor(initialData = null, apollo = null) {
		this.data = initialData ? Vue.observable(initialData) : null;
		this.apollo = apollo;
		this.pendingFields = new Set(); // Track fields that are being saved
		this.unsavedFields = new Set(); // Track fields with unsaved changes (saveImmediately: false)
		this.listeners = new Set(); // Event listeners
	}

	// Event system for Vue reactivity
	on(event, callback) {
		this.listeners.add({ event, callback });
	}

	emit(event, data) {
		this.listeners.forEach((listener) => {
			if (listener.event === event) {
				listener.callback(data);
			}
		});
	}

	// Get the current data (for Vue reactivity)
	getData() {
		return this.data;
	}

	normalizeFieldValueForGraphQL(value) {
		if (value && typeof value === "object" && typeof value.id === "string") {
			return value.id;
		}
		return value;
	}

	fieldValuesEqual(currentValue, nextValue) {
		return (
			this.normalizeFieldValueForGraphQL(currentValue) ===
			this.normalizeFieldValueForGraphQL(nextValue)
		);
	}

	_setLocalField(fieldPath, value) {
		if (fieldPath.startsWith("book.")) {
			const fieldName = fieldPath.replace("book.", "");

			// Ensure book object exists
			if (!this.data.book) {
				Vue.set(this.data, "book", {});
			}

			const newBook = {
				...this.data.book,
				[fieldName]: value,
			};

			Vue.set(this.data, "book", newBook);
		} else {
			const pathParts = fieldPath.split(".");
			let current = this.data;

			for (let i = 0; i < pathParts.length - 1; i++) {
				const part = pathParts[i];
				if (!current[part] || typeof current[part] !== "object") {
					Vue.set(current, part, {});
				}
				current = current[part];
			}

			const finalKey = pathParts[pathParts.length - 1];
			Vue.set(current, finalKey, value);
		}

		this.emit("field-updated", { fieldPath, value });
	}

	async _saveFieldToGraphQL(fieldName, value, options = {}) {
		if (!this.apollo || !this.getBookId()) {
			throw new Error("Apollo client or book ID not available");
		}

		this.pendingFields.add(fieldName);
		this.emit("field-saving", { fieldName, value });

		try {
			const input = {
				id: createIri(IRIS.localBook, this.getBookId()),
			};

			const graphQLValue = this.normalizeFieldValueForGraphQL(value);

			// Convert value to appropriate type
			if (typeof graphQLValue === "number") {
				input[fieldName] = parseFloat(graphQLValue) || 0;
			} else if (
				options.valueType === "number" &&
				typeof graphQLValue === "string" &&
				!isNaN(parseFloat(graphQLValue)) &&
				isFinite(graphQLValue)
			) {
				// Component explicitly indicated this should be a number
				input[fieldName] = parseFloat(graphQLValue);
			} else {
				input[fieldName] = graphQLValue;
			}

			const mutation = gql`
        mutation updateLocalBook($input: updateLocalBookInput!) {
          updateLocalBook(input: $input) {
            localBook {
              ...LocalBookDetails
            }
          }
        }
        ${LocalBookFragments.localBookDetailsFragment}
      `;

			const result = await this.apollo.mutate({
				mutation,
				variables: { input },
			});

			this.pendingFields.delete(fieldName);
			this.emit("field-saved", { fieldName, value, result });

			return result.data?.updateLocalBook?.localBook;
		} catch (error) {
			this.pendingFields.delete(fieldName);
			this.emit("field-error", { fieldName, value, error });
			throw error;
		}
	}

	async updateField(fieldPath, value, options = {}) {
		const { saveImmediately = true, valueType } = options;

		const currentValue = this.data?.book?.[fieldPath.replace("book.", "")];
		if (this.fieldValuesEqual(currentValue, value)) {
			return;
		}

		if (saveImmediately && fieldPath.startsWith("book.")) {
			const fieldName = fieldPath.replace("book.", "");

			try {
				const updatedBook = await this._saveFieldToGraphQL(fieldName, value, {
					valueType,
				});
				if (updatedBook && updatedBook[fieldName] !== undefined) {
					this._setLocalField(fieldPath, updatedBook[fieldName]);
				}
			} catch (error) {
				console.error(`Error saving field ${fieldName}:`, error);
				this.emit("field-error", { fieldName, value, error });
			}
		} else {
			this._setLocalField(fieldPath, value);

			if (fieldPath.startsWith("book.") && !saveImmediately) {
				const fieldName = fieldPath.replace("book.", "");
				this.unsavedFields.add(fieldName);
			}
		}
	}

	setPendingField(fieldPath, value) {
		this.updateField(fieldPath, value, { saveImmediately: false });
	}

	// For fields that were already saved externally (e.g., via separate mutations or REST calls)
	updateFieldAlreadySaved(fieldPath, value) {
		this._setLocalField(fieldPath, value);
	}

	getBookId() {
		return this.data?.id || this.data?.book?.id;
	}

	getBook() {
		return this.data?.book || {};
	}

	getTitle() {
		return this.getBook().title || "";
	}

	getPublisher() {
		return this.getBook().publisher || null;
	}

	getSubjects() {
		const subjects = this.getBook().subjects;
		if (!subjects) return [];

		// Handle both direct array and edges structure
		if (subjects.edges) {
			return subjects.edges;
		} else if (Array.isArray(subjects)) {
			return subjects;
		}

		return [];
	}

	getLanguage() {
		return this.getBook().language || null;
	}

	getPublishedDate() {
		return this.getBook().publishedOn || null;
	}

	getAuthors() {
		return this.getBook().bookAuthors || [];
	}

	// Validation methods
	hasRequiredAuthors() {
		const authors = this.getAuthors();
		return (
			authors.length > 0 &&
			authors.some(
				(bookAuthor) =>
					bookAuthor.author &&
					bookAuthor.author.name &&
					bookAuthor.role &&
					bookAuthor.role.description,
			)
		);
	}

	hasRequiredSubjects() {
		const subjects = this.getSubjects();
		return subjects.length > 0;
	}

	isUploadComplete() {
		return !!this.data;
	}

	isProductDetailsComplete() {
		return (
			this.isUploadComplete() &&
			!!this.getTitle().trim() &&
			this.hasRequiredAuthors()
		);
	}

	isPublicationDetailsComplete() {
		return (
			this.isProductDetailsComplete() &&
			!!this.getPublisher() &&
			this.hasRequiredSubjects() &&
			!!this.getLanguage() &&
			!!this.getPublishedDate()
		);
	}

	isPriceReady() {
		return this.isPublicationDetailsComplete();
	}

	isReviewReady() {
		return this.isPriceReady();
	}

	// Special methods for complex fields
	updatePublisher(publisher, saveImmediately = true) {
		this.updateField("book.publisher", publisher, { saveImmediately });
	}

	updateSubjects(subjects, saveImmediately = true) {
		// Create a structure similar to what the API returns
		const subjectsConnection = {
			edges: subjects.map((subject) => ({
				node: subject,
			})),
		};

		this.updateField("book.subjects", subjectsConnection, { saveImmediately });
	}

	// Set uploaded product data
	setUploadedData(uploadedData) {
		// Normalize the data structure
		const normalizedData =
			"book" in uploadedData ? uploadedData : { book: { ...uploadedData } };

		// Ensure all expected fields exist with null values for proper Vue reactivity
		// This prevents issues where fields are completely missing vs being null
		if (normalizedData.book) {
			const expectedFields = [
				"publisher",
				"language",
				"publishedOn",
				"description",
				"price",
				"isbn",
			];
			expectedFields.forEach((field) => {
				if (!(field in normalizedData.book)) {
					normalizedData.book[field] = null;
				}
			});
		}

		// Make the data reactive to Vue
		this.data = Vue.observable(normalizedData);
		this.emit("field-updated", normalizedData);
		this.emit("product-uploaded", normalizedData);
	}

	// Load product from GraphQL
	async loadProduct(productIri, LocalBookQueries) {
		if (!this.apollo) {
			console.error("Cannot load product: missing Apollo client");
			return;
		}

		if (!productIri) {
			return;
		}

		try {
			const result = await this.apollo.query({
				query: LocalBookQueries.getLocalBook,
				variables: { id: productIri },
			});

			if (result.data && result.data.localBook) {
				// Normalize the data structure
				const normalizedData =
					"book" in result.data.localBook
						? result.data.localBook
						: { book: { ...result.data.localBook } };

				this.data = normalizedData;
				this.emit("product-loaded", normalizedData);
				return normalizedData;
			}
		} catch (error) {
			console.error("Error loading product:", error);
			this.emit("product-load-error", error);
			throw error;
		}
	}

	// Debug helper
	getValidationStatus() {
		return {
			hasTitle: !!this.getTitle().trim(),
			hasAuthors: this.hasRequiredAuthors(),
			hasPublisher: !!this.getPublisher(),
			hasSubjects: this.hasRequiredSubjects(),
			hasLanguage: !!this.getLanguage(),
			hasPublishedDate: !!this.getPublishedDate(),
			isProductDetailsComplete: this.isProductDetailsComplete(),
			isPublicationDetailsComplete: this.isPublicationDetailsComplete(),
		};
	}

	hasPendingReleaseChanges() {
		return this.unsavedFields.has("availableFrom");
	}

	async publishProduct() {
		if (!this.getBookId()) {
			throw new Error("Cannot publish: no book ID available");
		}

		if (this.unsavedFields.size === 0) {
			return this.getData();
		}

		const input = {
			id: createIri(IRIS.localBook, this.getBookId()),
		};

		for (const fieldName of this.unsavedFields) {
			const fieldValue = this.getBook()[fieldName];
			if (fieldValue !== undefined) {
				input[fieldName] = this.normalizeFieldValueForGraphQL(fieldValue);
			}
		}

		const mutation = gql`
      mutation updateLocalBook($input: updateLocalBookInput!) {
        updateLocalBook(input: $input) {
          localBook {
            ...LocalBookDetails
          }
        }
      }
      ${LocalBookFragments.localBookDetailsFragment}
    `;

		const result = await this.apollo.mutate({
			mutation,
			variables: { input },
		});

		const publishedFields = Array.from(this.unsavedFields);
		this.emit("fields-published", { fields: publishedFields, result });

		this.unsavedFields.clear();

		return this.getData();
	}

	destroy() {
		this.unsavedFields.clear();
		this.listeners.clear();
	}
}
